import { FC, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Block, Timeline } from '@mui/icons-material'
import { Box, Card, CardContent, IconButton, Paper, Typography } from '@mui/material'

import { TagGroup } from '../../../../../api/core'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { WindowVirtualizedList } from '../../../../../components/WindowVirtualizedList/WindowVirtualizedList'
import { useIntersectionObserverEntry } from '../../../../../hooks/useIntersectionObserverEntry'
import { useCurrentUserLanguage } from '../../../../account/hooks/useCurrentUserLanguage'
import { EventDialogTab } from '../../../../live-events/hooks/useEventDialogTabs'
import { useLiveEventTypeModal } from '../../../../live-events/hooks/useLiveEventTypeModal'
import { TrackedGame, TrackedGameBlueprint } from '../../../../live-events/types/TrackedGame'
import { TrackingEventExample } from '../../../../live-events/types/TrackingEvents'
import { LiveEventCard } from '../LiveEventCard/LiveEventCard'
import './LiveEventBlueprintCard.scss'

type LiveEventBlueprintCardProps = {
  blueprintId: string | undefined
  eventTypeName: string
  events: TrackingEventExample[]
  games: { [gameId: string]: TrackedGame }
  tagGroups: { [eventTypeId: string]: TagGroup }
  tagGroupColor: string
  isDiscontinued?: boolean
}

/** Resolves the blueprint display name from tracked game blueprints */
const resolveBlueprintName = (blueprints: TrackedGameBlueprint[], blueprintId: string | undefined, userLanguage: string): string | undefined => {
  if (!blueprintId) return undefined

  const blueprint = blueprints.find((b) => b.blueprintId === blueprintId)
  if (!blueprint) return undefined

  return blueprint.names[userLanguage as keyof typeof blueprint.names] || blueprint.names['en'] || blueprintId
}

export const LiveEventBlueprintCard: FC<LiveEventBlueprintCardProps> = ({
  blueprintId,
  eventTypeName,
  events,
  games,
  tagGroups,
  tagGroupColor,
  isDiscontinued,
}) => {
  const { t } = useTranslation()
  const stickyRef = useRef<HTMLDivElement | null>(null)
  const userLanguage = useCurrentUserLanguage()
  const { showModal: showLiveEventTypeModal } = useLiveEventTypeModal()

  // Sort events by start time (newest first), filter to those with a tracked game
  const displayEvents = useMemo(() => {
    const sorted = [...events].sort((a, b) => b.start - a.start)
    return sorted.filter((event) => games[event.gameId] != null)
  }, [events, games])

  // Resolve blueprint name from the first event's game blueprints
  const blueprintName = useMemo(() => {
    if (!blueprintId) return t('live-events:no_blueprint')

    // Find any tracked game that has blueprint data to resolve the name
    for (const event of events) {
      const trackedGame = games[event.gameId]
      if (trackedGame?.blueprints) {
        const resolved = resolveBlueprintName(trackedGame.blueprints, blueprintId, userLanguage)
        if (resolved) return resolved
      }
    }

    return blueprintId
  }, [blueprintId, events, games, userLanguage, t])

  // Handle opening the Evolution tab for this blueprint
  const handleOpenEvolution = () => {
    const firstEvent = displayEvents[0]
    if (!firstEvent) return

    showLiveEventTypeModal({
      trackedGameId: firstEvent.gameId,
      eventTypeId: firstEvent.typeId,
      tab: EventDialogTab.LiveEventEvolution,
      eventId: firstEvent.eventId,
    })
  }

  // Use intersection observer to detect when element becomes sticky
  useIntersectionObserverEntry(
    stickyRef,
    {
      rootMargin: '-144px 0px 0px 0px',
      threshold: [1],
    },
    (entry) => {
      const { target, intersectionRatio } = entry
      target.classList.toggle('LiveEventBlueprintCard__paper--sticky', intersectionRatio < 1)
    }
  )

  return (
    <Box className="LiveEventBlueprintCard__container">
      <Paper
        ref={stickyRef}
        elevation={1}
        className="LiveEventBlueprintCard__paper"
        sx={{
          borderTop: `5px solid ${tagGroupColor}`,
        }}
      >
        <Box className="LiveEventBlueprintCard__header-left">
          {isDiscontinued && (
            <GRTooltip content={t('live-events:live_event_evolution_type_discontinued')} styles={{ height: '20px', width: '20px', zIndex: 1000 }}>
              <Block color="error" fontSize="small" />
            </GRTooltip>
          )}
          <Typography variant="h6">{blueprintName}</Typography>

          <Typography variant="body2" color="text.secondary" noWrap>
            {eventTypeName} ({t('common:event_count', { count: displayEvents.length })})
          </Typography>
        </Box>

        {blueprintId && (
          <GRTooltip content={t('implementations:view_evolution')}>
            <IconButton size="small" color="primary" onClick={handleOpenEvolution}>
              <Timeline />
            </IconButton>
          </GRTooltip>
        )}
      </Paper>

      <Card className="LiveEventBlueprintCard__card">
        <CardContent className="LiveEventTypeCard__card-content">
          <WindowVirtualizedList<TrackingEventExample>
            items={displayEvents}
            columns={{ xs: 1, sm: 2, md: 3 }}
            gap={16}
            estimateSize={360}
            getItemKey={(event) => event.eventId}
            renderItem={(event) => {
              const trackedGame = games[event.gameId]
              if (!trackedGame) return null
              return <LiveEventCard event={event} trackedGame={trackedGame} tagGroup={tagGroups[event.typeId]} isNew={event.highlighted} />
            }}
            noItemsLabel={null}
            containerProps={{ className: 'LiveEventTypeCard__grid-container' }}
          />
        </CardContent>
      </Card>
    </Box>
  )
}
